/**
 * Hash routing for the messenger (UX review follow-up, 2026-02-10).
 *
 * The in-memory route stays the source of truth; these pure helpers mirror it
 * into the URL hash so browser Back steps screens, deep links work, and a
 * refresh restores where you were. Pure functions — unit-tested, no React.
 */

import type { RelationshipWithPeer } from '../relationships/api';

export type Route =
  | { name: 'conversations' }
  /** Phones, noticeboard variant of the home panel test: the Desk tab. */
  | { name: 'desk' }
  | { name: 'conversation'; relationshipId: string; peer?: RelationshipWithPeer['peer'] }
  | {
      name: 'summary';
      relationshipId: string;
      peer?: RelationshipWithPeer['peer'];
      /** The route that opened the summary — Back returns there when known. */
      back?: Route;
    }
  | {
      /** The pair's photos, pinned up as Polaroids. */
      name: 'scrapbook';
      relationshipId: string;
      peer?: RelationshipWithPeer['peer'];
      back?: Route;
    }
  | {
      name: 'letter';
      letterId: string;
      relationshipId: string;
      peer?: RelationshipWithPeer['peer'];
      openNudge?: boolean;
    };

export function routeToHash(r: Route): string {
  switch (r.name) {
    case 'conversation':
      return `#/c/${r.relationshipId}`;
    case 'summary':
      return `#/s/${r.relationshipId}`;
    case 'scrapbook':
      return `#/p/${r.relationshipId}`;
    case 'letter':
      return `#/l/${r.letterId}?rel=${r.relationshipId}${r.openNudge ? '&nudge=1' : ''}`;
    case 'desk':
      return '#/desk';
    default:
      return '';
  }
}

export function hashToRoute(
  hash: string,
  relationships?: RelationshipWithPeer[],
): Route {
  const path = hash.replace(/^#\/?/, '');
  const [kind, id = ''] = path.split(/[/?]/);
  const params = new URLSearchParams(hash.split('?')[1] ?? '');
  const relId = params.get('rel') ?? id;
  const peer = relationships?.find((r) => r.id === relId)?.peer ?? null;
  if (kind === 'desk') return { name: 'desk' };
  if (kind === 'c' && id) return { name: 'conversation', relationshipId: id, peer };
  if (kind === 's' && id) return { name: 'summary', relationshipId: id, peer };
  if (kind === 'p' && id) return { name: 'scrapbook', relationshipId: id, peer };
  if (kind === 'l' && id && relId) {
    return {
      name: 'letter',
      letterId: id,
      relationshipId: relId,
      peer,
      openNudge: params.get('nudge') === '1' ? true : undefined,
    };
  }
  return { name: 'conversations' };
}

/** The canonical serialization of a route — used to dedupe hash echoes. */
export function routeKey(r: Route): string {
  return JSON.stringify([
    r.name,
    'relationshipId' in r ? r.relationshipId : '',
    'letterId' in r ? r.letterId : '',
    'openNudge' in r && r.openNudge ? 1 : 0,
  ]);
}
/**
 * Hashes that belong to app-level screens rather than the messenger — the
 * stamp book, the admin dashboard, and the member's profile. The messenger
 * leaves them alone.
 */
export const PROFILE_HASH = '#/profile';

export function isAppScreenHash(hash: string): boolean {
  return /^#\/(stamps|admin|profile)(\/|\?|$)/.test(hash);
}
